"use client";

import { forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef } from "react";
import clsx from "clsx";
import intlTelInput, { type Iso2, type Iti } from "intl-tel-input/intlTelInputWithUtils";
import { ar as arPhoneTranslations, en as enPhoneTranslations } from "intl-tel-input/i18n";

export type IntlPhoneInputHandle = {
  /**
   * Country-aware format check via the underlying intl-tel-input/
   * libphonenumber utils. Empty input is treated as valid here - "required"
   * is the form schema's concern, this only judges format once something
   * has actually been entered.
   */
  isValid: () => boolean;
};

type Props = {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange: (value: string) => void;
  onCountryChange?: (countryCode: string) => void;
  error?: string;
  locale?: string;
  country?: string;
  defaultCountry?: string;
  disabled?: boolean;
  name?: string;
  onBlur?: () => void;
  className?: string;
  inputClassName?: string;
};

/**
 * Both dial codes the picker lists for Palestine (+972 and +970) are named
 * "Palestine". The library otherwise takes the names from the browser's
 * Intl.DisplayNames, which has no option for this.
 */
const COUNTRY_NAME_OVERRIDES: Record<"en" | "ar", Partial<Record<Iso2, string>>> = {
  en: { il: "Palestine", ps: "Palestine" },
  ar: { il: "فلسطين", ps: "فلسطين" },
};

/**
 * The country the rest of the app records for a picked dial code. The app
 * lists Palestine once, as PS, so a +972 number reports PS too - otherwise
 * a form that fills its country from the phone would look for a country it
 * no longer offers.
 */
const REPORTED_COUNTRY_ALIASES: Record<string, string> = {
  IL: "PS",
};

function getRegionFromLocale(localeValue: string): string | null {
  try {
    return new Intl.Locale(localeValue).region?.toLowerCase() ?? null;
  } catch {
    const region = localeValue.split(/[-_]/)[1];
    return region ? region.toLowerCase() : null;
  }
}

function normalizeCountryCode(countryCode?: string | null): Iso2 | null {
  if (!countryCode) {
    return null;
  }

  const normalized = countryCode.toLowerCase() as Iso2;
  const hasCountry = intlTelInput.getCountryData().some(({ iso2 }) => iso2 === normalized);

  return hasCountry ? normalized : null;
}

function detectUserCountryCode(): Iso2 | null {
  if (typeof window === "undefined") {
    return null;
  }

  const localeCandidates = [navigator.language, ...navigator.languages].filter(Boolean);

  for (const localeValue of localeCandidates) {
    const detectedCountry = normalizeCountryCode(getRegionFromLocale(localeValue));

    if (detectedCountry) {
      return detectedCountry;
    }
  }

  return null;
}

const IntlPhoneInput = forwardRef<IntlPhoneInputHandle, Props>(function IntlPhoneInput({
  label,
  placeholder,
  value = "",
  onChange,
  onCountryChange,
  error,
  locale = "en",
  country,
  defaultCountry,
  disabled = false,
  name,
  onBlur,
  className,
  inputClassName,
}: Props, ref) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const itiRef = useRef<Iti | null>(null);
  const onChangeRef = useRef(onChange);
  const onCountryChangeRef = useRef(onCountryChange);
  const onBlurRef = useRef(onBlur);
  const countryRef = useRef(country);
  const defaultCountryRef = useRef(defaultCountry);
  const activeLocale = locale === "ar" ? "ar" : "en";
  const translations = useMemo(
    () => (activeLocale === "ar" ? arPhoneTranslations : enPhoneTranslations),
    [activeLocale]
  );

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onCountryChangeRef.current = onCountryChange;
  }, [onCountryChange]);

  useEffect(() => {
    onBlurRef.current = onBlur;
  }, [onBlur]);

  useEffect(() => {
    countryRef.current = country;
  }, [country]);

  useEffect(() => {
    defaultCountryRef.current = defaultCountry;
  }, [defaultCountry]);

  useEffect(() => {
    const input = inputRef.current;

    if (!input) {
      return;
    }

    const initialCountry =
      normalizeCountryCode(countryRef.current) ??
      normalizeCountryCode(defaultCountryRef.current) ??
      detectUserCountryCode() ??
      (activeLocale === "ar" ? "eg" : "us");

    const emitValue = () => {
      const nextValue = itiRef.current?.getNumber() || input.value.trim();
      onChangeRef.current(nextValue);
    };

    const emitCountry = () => {
      const selectedCountry = itiRef.current?.getSelectedCountryData()?.iso2?.toUpperCase();
      const nextCountry = selectedCountry
        ? (REPORTED_COUNTRY_ALIASES[selectedCountry] ?? selectedCountry)
        : undefined;

      if (nextCountry) {
        onCountryChangeRef.current?.(nextCountry);
      }
    };

    const iti = intlTelInput(input, {
      initialCountry,
      countryNameLocale: activeLocale,
      countryNameOverrides: COUNTRY_NAME_OVERRIDES[activeLocale],
      i18n: translations,
      separateDialCode: true,
      // Append the country dropdown to <body> so it escapes the form's
      // per-field stacking contexts (sibling selects carry `z-10`), which
      // otherwise trap the inline dropdown beneath them.
      dropdownContainer: document.body,
    });

    itiRef.current = iti;

    const handleInput = () => {
      emitValue();
    };

    const handleCountryChange = () => {
      emitCountry();
      emitValue();
    };

    const handleBlur = () => {
      onBlurRef.current?.();
    };

    input.addEventListener("input", handleInput);
    input.addEventListener("countrychange", handleCountryChange);
    input.addEventListener("blur", handleBlur);

    void iti.promise.finally(() => {
      emitCountry();
      emitValue();
    });

    return () => {
      input.removeEventListener("input", handleInput);
      input.removeEventListener("countrychange", handleCountryChange);
      input.removeEventListener("blur", handleBlur);
      iti.destroy();
      itiRef.current = null;
    };
  }, [activeLocale, defaultCountry, translations]);

  useEffect(() => {
    const iti = itiRef.current;

    if (!iti) {
      return;
    }

    const normalizedCountry = normalizeCountryCode(country);

    if (normalizedCountry && iti.getSelectedCountryData()?.iso2 !== normalizedCountry) {
      iti.setCountry(normalizedCountry);
    }
  }, [country]);

  useEffect(() => {
    const iti = itiRef.current;
    const input = inputRef.current;

    if (!iti || !input) {
      return;
    }

    const currentNumber = iti.getNumber() || input.value.trim();

    if (value && currentNumber !== value) {
      iti.setNumber(value);
      return;
    }

    if (!value && currentNumber) {
      iti.setNumber("");
    }
  }, [value]);

  useEffect(() => {
    const iti = itiRef.current;

    if (iti) {
      iti.setDisabled(disabled);
    }
  }, [disabled]);

  useImperativeHandle(ref, () => ({
    isValid: () => {
      const iti = itiRef.current;

      if (!iti || !iti.getNumber()) {
        return true;
      }

      return iti.isValidNumber() === true;
    },
  }), []);

  return (
    <div className={clsx("phone-input", className)}>
      {label ? (
        <label htmlFor={inputId} className="mb-1.5 block text-sm text-foreground">
          {label}
        </label>
      ) : null}

      <input
        id={inputId}
        ref={inputRef}
        name={name}
        type="tel"
        placeholder={placeholder}
        disabled={disabled}
        className={clsx(
          "w-full h-11 px-3 text-sm rounded-lg border bg-input text-foreground placeholder:text-text-muted transition-colors duration-150 focus:outline-none border-border focus:border-primary focus:ring-2 focus:ring-focus-ring/50 disabled:bg-hover disabled:text-text-muted disabled:cursor-not-allowed disabled:border-border-subtle",
          "disabled:bg-hover disabled:text-text-muted disabled:cursor-not-allowed disabled:border-border-subtle",
          error ? "border-danger bg-danger/5" : "border-border",
          inputClassName
        )}
      />

      {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}
    </div>
  );
});

export default IntlPhoneInput;
